<script setup>
/**
 * PromotionEditor Component
 * Full editor for creating/editing promotions
 * Drawer on desktop, full-screen modal on mobile
 */
import { ref, computed, watch, onMounted } from 'vue'
import PromotionItemsPicker from './PromotionItemsPicker.vue'
import PromotionStepsEditor from './PromotionStepsEditor.vue'
import {
  getSaleById,
  createSale,
  updateSale,
  deleteSale,
  timestampToStringDate,
} from '../../../../core/services/sales.js'
import { useConfirmDialog } from '@core/composables/useConfirmDialog.js'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  saleId: {
    type: String,
    default: null,
  },
  merakez: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['close', 'saved', 'deleted'])
const { confirm } = useConfirmDialog()

// ============================================
// State
// ============================================
const isLoading = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)
const loadError = ref(null)
const saveError = ref(null)
const activeTab = ref('general')
const isDirty = ref(false)
const originalData = ref(null)

// Delete confirmation
const showDeleteConfirm = ref(false)
const deleteConfirmText = ref('')

// Form data
const formData = ref({
  // General
  label: '',
  description: '',
  saleType: 'madregot',
  active: false,
  approved: false,
  first_order: false,
  orderDay: false,
  weeklyBenefit: false,
  max: null,
  image: '',
  // Dates
  start_date: '',
  end_date: '',
  // Items
  items: [],
  // Steps
  step_1: { amount: '', price: '', description: '' },
  step_2: { amount: '', price: '', description: '' },
  step_3: { amount: '', price: '', description: '' },
  step_4: { amount: '', price: '', description: '' },
  step_5: { amount: '', price: '', description: '' },
})

// Keep track of unknown fields from original doc
const unknownFields = ref({})

// ============================================
// Computed
// ============================================
const isEditMode = computed(() => !!props.saleId)
const title = computed(() => (isEditMode.value ? 'עריכת מבצע' : 'מבצע חדש'))

const tabs = computed(() => [
  { id: 'general', label: 'כללי', disabled: false },
  { id: 'dates', label: 'תאריכים', disabled: false },
  { id: 'items', label: 'פריטים', disabled: false },
  { id: 'steps', label: 'מדרגות', disabled: formData.value.saleType !== 'madregot' },
])

const saleTypeOptions = [{ value: 'madregot', label: 'מדרגות' }]

const dateError = computed(() => {
  if (!formData.value.start_date || !formData.value.end_date) return null
  if (formData.value.start_date > formData.value.end_date) {
    return 'תאריך התחלה חייב להיות לפני תאריך סיום'
  }
  return null
})

const canSave = computed(() => {
  return !isSaving.value && !dateError.value && formData.value.label.trim()
})

const canDelete = computed(() => {
  return deleteConfirmText.value.toUpperCase() === 'DELETE'
})

// ============================================
// Watchers
// ============================================
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      loadSaleData()
    } else {
      resetForm()
    }
  }
)

// Track dirty state
watch(
  formData,
  () => {
    if (originalData.value) {
      isDirty.value = JSON.stringify(formData.value) !== JSON.stringify(originalData.value)
    }
  },
  { deep: true }
)

// ============================================
// Load Sale Data
// ============================================
async function loadSaleData() {
  if (!props.saleId) {
    // New sale - reset form
    resetForm()
    return
  }

  isLoading.value = true
  loadError.value = null

  try {
    const sale = await getSaleById(props.saleId)
    if (!sale) {
      loadError.value = 'המבצע לא נמצא'
      return
    }

    // Extract known fields
    const knownKeys = [
      'id',
      'uid',
      'merakez',
      'label',
      'description',
      'saleType',
      'active',
      'approved',
      'first_order',
      'orderDay',
      'weeklyBenefit',
      'max',
      'image',
      'start_date',
      'end_date',
      'date_start_date',
      'date_end_date',
      'items',
      'makats_arr',
      'step_1',
      'step_2',
      'step_3',
      'step_4',
      'step_5',
      'item_kabel',
      'item_lekabel_amount',
      'kne_kabel_amount',
      'kne_kabel_items',
      'kabel_item_selected',
      'created_at',
      'updated_at',
    ]

    // Store unknown fields for preservation
    const unknown = {}
    for (const key of Object.keys(sale)) {
      if (!knownKeys.includes(key)) {
        unknown[key] = sale[key]
      }
    }
    unknownFields.value = unknown

    // Populate form data
    formData.value = {
      label: sale.label || '',
      description: sale.description || '',
      saleType: sale.saleType || 'madregot',
      active: sale.active ?? false,
      approved: sale.approved ?? false,
      first_order: sale.first_order ?? false,
      orderDay: sale.orderDay ?? false,
      weeklyBenefit: sale.weeklyBenefit ?? false,
      max: sale.max ?? null,
      image: sale.image || '',
      // Convert timestamps to strings if needed
      start_date: sale.start_date || timestampToStringDate(sale.date_start_date) || '',
      end_date: sale.end_date || timestampToStringDate(sale.date_end_date) || '',
      items: sale.items || [],
      step_1: sale.step_1 || { amount: '', price: '', description: '' },
      step_2: sale.step_2 || { amount: '', price: '', description: '' },
      step_3: sale.step_3 || { amount: '', price: '', description: '' },
      step_4: sale.step_4 || { amount: '', price: '', description: '' },
      step_5: sale.step_5 || { amount: '', price: '', description: '' },
    }

    // Store original for dirty checking
    originalData.value = JSON.parse(JSON.stringify(formData.value))
    isDirty.value = false
  } catch (err) {
    console.error('[PromotionEditor] Load failed:', err)
    loadError.value = 'שגיאה בטעינת המבצע'
  } finally {
    isLoading.value = false
  }
}

// ============================================
// Save
// ============================================
async function handleSave() {
  if (!canSave.value) return

  isSaving.value = true
  saveError.value = null

  try {
    // Build payload
    const payload = {
      ...formData.value,
      ...unknownFields.value, // Preserve unknown fields
    }

    if (isEditMode.value) {
      await updateSale(props.saleId, payload)
    } else {
      await createSale(props.merakez, payload)
    }

    emit('saved')
    emit('close')
  } catch (err) {
    console.error('[PromotionEditor] Save failed:', err)
    saveError.value = err.message || 'שגיאה בשמירה'
  } finally {
    isSaving.value = false
  }
}

// ============================================
// Delete
// ============================================
function openDeleteConfirm() {
  showDeleteConfirm.value = true
  deleteConfirmText.value = ''
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  deleteConfirmText.value = ''
}

async function handleDelete() {
  if (!canDelete.value) return

  isDeleting.value = true

  try {
    await deleteSale(props.saleId)
    emit('deleted')
    emit('close')
  } catch (err) {
    console.error('[PromotionEditor] Delete failed:', err)
    saveError.value = err.message || 'שגיאה במחיקה'
  } finally {
    isDeleting.value = false
    closeDeleteConfirm()
  }
}

// ============================================
// Close
// ============================================
async function handleClose() {
  if (isDirty.value) {
    const ok = await confirm({
      title: 'שינויים שלא נשמרו',
      message: 'יש שינויים שלא נשמרו. האם לצאת בכל זאת?',
      confirmText: 'צא ללא שמירה',
      variant: 'warning',
    })
    if (!ok) return
  }
  emit('close')
}

// ============================================
// Reset
// ============================================
function resetForm() {
  activeTab.value = 'general'
  isDirty.value = false
  originalData.value = null
  unknownFields.value = {}
  loadError.value = null
  saveError.value = null
  showDeleteConfirm.value = false
  deleteConfirmText.value = ''

  formData.value = {
    label: '',
    description: '',
    saleType: 'madregot',
    active: false,
    approved: false,
    first_order: false,
    orderDay: false,
    weeklyBenefit: false,
    max: null,
    image: '',
    start_date: '',
    end_date: '',
    items: [],
    step_1: { amount: '', price: '', description: '' },
    step_2: { amount: '', price: '', description: '' },
    step_3: { amount: '', price: '', description: '' },
    step_4: { amount: '', price: '', description: '' },
    step_5: { amount: '', price: '', description: '' },
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer">
      <div v-if="isOpen" class="editor-overlay" @click.self="handleClose">
        <div class="editor-drawer">
          <!-- Header -->
          <div class="editor-header">
            <h2 class="editor-title">{{ title }}</h2>
            <span class="merakez-badge">{{ merakez }}</span>
            <button class="close-btn" @click="handleClose">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="editor-loading">
            <div class="loading-spinner"></div>
            <span>טוען מבצע...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="loadError" class="editor-error">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>{{ loadError }}</span>
            <button class="retry-btn" @click="loadSaleData">נסה שנית</button>
          </div>

          <!-- Content -->
          <template v-else>
            <!-- Tabs -->
            <div class="editor-tabs">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                class="tab-btn"
                :class="{ active: activeTab === tab.id, disabled: tab.disabled }"
                :disabled="tab.disabled"
                @click="!tab.disabled && (activeTab = tab.id)"
              >
                {{ tab.label }}
              </button>
            </div>

            <!-- Tab Content -->
            <div class="editor-content">
              <!-- General Tab -->
              <div v-show="activeTab === 'general'" class="tab-panel">
                <div class="form-group">
                  <label class="form-label">שם המבצע *</label>
                  <input
                    v-model="formData.label"
                    type="text"
                    class="form-input"
                    placeholder="הזן שם מבצע..."
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">תיאור</label>
                  <textarea
                    v-model="formData.description"
                    class="form-textarea"
                    rows="3"
                    placeholder="תיאור המבצע..."
                  ></textarea>
                </div>

                <div class="form-group">
                  <label class="form-label">סוג מבצע</label>
                  <select v-model="formData.saleType" class="form-select">
                    <option v-for="opt in saleTypeOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </div>

                <!-- Max units limit (applies to any promotion type) -->
                <div class="form-group">
                  <label class="form-label">הגבלת כמות ביחידות</label>
                  <input
                    v-model.number="formData.max"
                    type="number"
                    class="form-input form-input-short"
                    min="0"
                    placeholder="ללא הגבלה"
                  />
                  <p class="form-hint">ריק = ללא הגבלה. יחידות מעבר לכמות יחויבו במחיר רגיל.</p>
                </div>

                <div class="form-row">
                  <label class="form-toggle">
                    <input type="checkbox" v-model="formData.active" />
                    <span class="toggle-label">פעיל</span>
                  </label>

                  <label class="form-toggle">
                    <input type="checkbox" v-model="formData.approved" />
                    <span class="toggle-label">מאושר</span>
                  </label>

                  <label class="form-toggle">
                    <input type="checkbox" v-model="formData.first_order" />
                    <span class="toggle-label">מצטרפים חדשים</span>
                  </label>

                  <label class="form-toggle">
                    <input type="checkbox" v-model="formData.orderDay" />
                    <span class="toggle-label">יום ההזמנה</span>
                  </label>

                  <label class="form-toggle">
                    <input type="checkbox" v-model="formData.weeklyBenefit" />
                    <span class="toggle-label">הטבה שבועית</span>
                  </label>
                </div>
                <p v-if="formData.weeklyBenefit" class="form-hint">
                  הטבה שבועית נשלחת בוואטסאפ ביום ההזמנה של כל לקוח במרכז של המבצע (רק לקוחות עם
                  משתמש באפליקציה שאישרו תנאי שימוש).
                </p>
              </div>

              <!-- Dates Tab -->
              <div v-show="activeTab === 'dates'" class="tab-panel">
                <div class="form-group">
                  <label class="form-label">תאריך התחלה</label>
                  <input v-model="formData.start_date" type="date" class="form-input" dir="ltr" />
                </div>

                <div class="form-group">
                  <label class="form-label">תאריך סיום</label>
                  <input v-model="formData.end_date" type="date" class="form-input" dir="ltr" />
                </div>

                <div v-if="dateError" class="form-error">
                  {{ dateError }}
                </div>

                <p class="form-hint">
                  התאריכים נשמרים גם כטקסט (YYYY-MM-DD) וגם כ-Timestamp לתאימות מלאה
                </p>
              </div>

              <!-- Items Tab -->
              <div v-show="activeTab === 'items'" class="tab-panel">
                <PromotionItemsPicker v-model:items="formData.items" />

                <!-- Image Selection (only when items exist) -->
                <div v-if="formData.items.length > 0" class="image-selection">
                  <div class="image-selection-header">
                    <span class="image-selection-title">תמונת המבצע</span>
                    <span class="image-selection-hint">בחר פריט שישמש כתמונה מייצגת</span>
                  </div>
                  <div class="image-selection-grid">
                    <button
                      v-for="item in formData.items"
                      :key="item.makat"
                      class="image-option"
                      :class="{ selected: formData.image === item.image && item.image }"
                      :disabled="!item.image"
                      @click="item.image && (formData.image = item.image)"
                    >
                      <div class="image-option-preview">
                        <img v-if="item.image" :src="item.image" :alt="item.name" />
                        <div v-else class="no-image">
                          <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </div>
                      </div>
                      <span class="image-option-name">{{ item.name }}</span>
                      <span class="image-option-makat">{{ item.makat }}</span>
                      <div
                        v-if="formData.image === item.image && item.image"
                        class="selected-badge"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    </button>
                  </div>
                  <button
                    v-if="formData.image"
                    class="clear-image-btn"
                    @click="formData.image = ''"
                  >
                    נקה תמונה
                  </button>
                </div>
              </div>

              <!-- Steps Tab -->
              <div v-show="activeTab === 'steps'" class="tab-panel">
                <PromotionStepsEditor
                  v-model:step1="formData.step_1"
                  v-model:step2="formData.step_2"
                  v-model:step3="formData.step_3"
                  v-model:step4="formData.step_4"
                  v-model:step5="formData.step_5"
                />
              </div>
            </div>

            <!-- Footer -->
            <div class="editor-footer">
              <div v-if="saveError" class="footer-error">{{ saveError }}</div>

              <div class="footer-actions">
                <button
                  v-if="isEditMode"
                  class="delete-btn"
                  :disabled="isSaving || isDeleting"
                  @click="openDeleteConfirm"
                >
                  מחק מבצע
                </button>

                <div class="footer-spacer"></div>

                <button class="cancel-btn" :disabled="isSaving" @click="handleClose">ביטול</button>

                <button class="save-btn" :disabled="!canSave" @click="handleSave">
                  <span v-if="isSaving">שומר...</span>
                  <span v-else>שמור</span>
                </button>
              </div>
            </div>
          </template>

          <!-- Delete Confirmation Modal -->
          <Transition name="fade">
            <div
              v-if="showDeleteConfirm"
              class="delete-modal-overlay"
              @click.self="closeDeleteConfirm"
            >
              <div class="delete-modal">
                <h3 class="delete-modal-title">מחיקת מבצע</h3>
                <p class="delete-modal-text">פעולה זו תמחק את המבצע לצמיתות ולא ניתן לבטלה.</p>
                <p class="delete-modal-text">הקלד <strong>DELETE</strong> לאישור:</p>
                <input
                  v-model="deleteConfirmText"
                  type="text"
                  class="delete-confirm-input"
                  dir="ltr"
                  placeholder="DELETE"
                />
                <div class="delete-modal-actions">
                  <button class="cancel-btn" @click="closeDeleteConfirm">ביטול</button>
                  <button
                    class="confirm-delete-btn"
                    :disabled="!canDelete || isDeleting"
                    @click="handleDelete"
                  >
                    <span v-if="isDeleting">מוחק...</span>
                    <span v-else>מחק לצמיתות</span>
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Overlay */
.editor-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-start;
  z-index: 1000;
}

/* Drawer */
.editor-drawer {
  width: 100%;
  max-width: 600px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

/* Header */
.editor-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.editor-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.merakez-badge {
  font-size: var(--font-size-sm);
  font-family: var(--font-family-mono, monospace);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
}

.close-btn {
  margin-right: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

/* Loading / Error */
.editor-loading,
.editor-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  flex: 1;
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

.editor-error {
  color: var(--color-error);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.retry-btn {
  padding: 8px 16px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--color-primary);
  background: transparent;
  border: 1px solid var(--color-primary);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.retry-btn:hover {
  background: rgba(99, 102, 241, 0.1);
}

/* Tabs */
.editor-tabs {
  display: flex;
  gap: 2px;
  padding: var(--spacing-xs) var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  overflow-x: auto;
}

.tab-btn {
  padding: 8px 16px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--transition-fast);
}

.tab-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.tab-btn.active {
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
}

.tab-btn.disabled {
  color: var(--text-muted);
  opacity: 0.5;
  cursor: not-allowed;
}

.tab-btn.disabled:hover {
  background: transparent;
  color: var(--text-muted);
}

/* Content */
.editor-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-lg);
}

.tab-panel {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Form Elements */
.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.form-input,
.form-textarea {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  outline: none;
  transition: border-color var(--transition-fast);
}

.form-input:focus,
.form-textarea:focus {
  border-color: var(--color-primary);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-input-short {
  max-width: 150px;
}

.form-select {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  outline: none;
  cursor: pointer;
  transition: border-color var(--transition-fast);
}

.form-select:focus {
  border-color: var(--color-primary);
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
}

.form-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  cursor: pointer;
}

.form-toggle input {
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--color-primary);
}

.toggle-label {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.form-error {
  padding: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.1);
  border-radius: var(--border-radius);
}

.form-hint {
  font-size: 11px;
  color: var(--text-muted);
  margin: 0;
}

/* Image Selection */
.image-selection {
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--border-color);
}

.image-selection-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.image-selection-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.image-selection-hint {
  font-size: 11px;
  color: var(--text-muted);
}

.image-selection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: var(--spacing-sm);
}

.image-option {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: var(--spacing-sm);
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.image-option:hover:not(:disabled) {
  border-color: var(--color-primary);
}

.image-option.selected {
  border-color: var(--color-primary);
  background: rgba(99, 102, 241, 0.05);
}

.image-option:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-option-preview {
  width: 60px;
  height: 60px;
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-option-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  color: var(--text-muted);
}

.image-option-name {
  font-size: 11px;
  color: var(--text-primary);
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-option-makat {
  font-size: 10px;
  font-family: var(--font-family-mono, monospace);
  color: var(--text-muted);
}

.selected-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  border-radius: var(--border-radius-full);
}

.clear-image-btn {
  margin-top: var(--spacing-sm);
  padding: 6px 12px;
  font-size: 12px;
  font-family: var(--font-family);
  color: var(--text-muted);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-image-btn:hover {
  color: var(--text-primary);
  border-color: var(--text-secondary);
}

/* Footer */
.editor-footer {
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
}

.footer-error {
  margin-bottom: var(--spacing-sm);
  padding: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.1);
  border-radius: var(--border-radius);
  text-align: center;
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.footer-spacer {
  flex: 1;
}

.cancel-btn,
.save-btn,
.delete-btn {
  padding: 10px 20px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.cancel-btn {
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--border-color);
}

.cancel-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.save-btn {
  color: white;
  background: var(--color-primary);
  border: none;
}

.save-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.delete-btn {
  color: var(--color-error);
  background: transparent;
  border: 1px solid var(--color-error);
}

.delete-btn:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
}

/* Delete Modal */
.delete-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.delete-modal {
  width: 90%;
  max-width: 400px;
  padding: var(--spacing-lg);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.delete-modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-error);
  margin: 0 0 var(--spacing-md) 0;
}

.delete-modal-text {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-sm) 0;
}

.delete-confirm-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  font-family: var(--font-family-mono, monospace);
  text-align: center;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 2px solid var(--color-error);
  border-radius: var(--border-radius);
  outline: none;
  margin-bottom: var(--spacing-md);
}

.delete-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
}

.confirm-delete-btn {
  padding: 10px 20px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: white;
  background: var(--color-error);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.confirm-delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-delete-btn:hover:not(:disabled) {
  opacity: 0.9;
}

/* Transitions */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-enter-active .editor-drawer,
.drawer-leave-active .editor-drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .editor-drawer,
.drawer-leave-to .editor-drawer {
  transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Mobile: Full screen */
@media (max-width: 640px) {
  .editor-drawer {
    max-width: 100%;
  }

  .editor-tabs {
    padding: var(--spacing-xs);
  }

  .tab-btn {
    padding: 6px 12px;
    font-size: 12px;
  }

  .editor-content {
    padding: var(--spacing-md);
  }

  .form-row {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
}
</style>
